<template>
  <div class="relative" ref="rootEl">
    <button
      @click="isOpen = !isOpen"
      class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors"
      :class="activeCount > 0
        ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        : 'border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark'"
    >
      {{ label }}
      <span v-if="activeCount > 0" class="text-[10px] font-semibold">({{ activeCount }})</span>
      <ChevronDownIcon class="h-3 w-3 opacity-60" />
    </button>

    <div v-if="isOpen" class="absolute left-0 top-full mt-1 z-50 w-48 max-h-64 overflow-y-auto rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg">
      <FilterOptionList :options="options" @toggle="$emit('toggle', $event)" @clear="$emit('clear')" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import FilterOptionList from './FilterOptionList.vue'

const props = defineProps({
  label:   { type: String, required: true },
  options: { type: Array,  default: () => [] },  // [{ value, label, active, dot? }]
})
defineEmits(['toggle', 'clear'])

// v-model:open — controllable from outside (e.g. the "t" shortcut opening the
// Teachers dropdown), defaults to a local ref when unbound.
const isOpen = defineModel('open', { default: false })
const rootEl = ref(null)
const activeCount = computed(() => props.options.filter(o => o.active).length)

// Skip when this copy isn't rendered (display:none): CalendarFilterBar mounts both a
// labeled and an icon-only Teachers dropdown sharing one v-model:open, and the hidden
// one treating every click as "outside" closed the visible one mid-click, before the
// option's own click handler could fire.
const onClickOutside = e => {
  if (rootEl.value && rootEl.value.getClientRects().length && !rootEl.value.contains(e.target)) isOpen.value = false
}
// Esc with text in a FilterOptionList search box is left to that box (it clears the
// query); only an Esc on an empty box closes the dropdown. Checked by attribute, not
// rootEl, since CalendarFilterBar's hidden twin dropdown shares this open state.
const onKeydown = e => {
  if (e.key === 'Escape' && e.target?.hasAttribute?.('data-filter-search') && e.target.value) return
  if (e.key === 'Escape' && isOpen.value) {
    e.stopPropagation()
    isOpen.value = false
  }
}
onMounted(() => {
  document.addEventListener('click', onClickOutside, true)
  document.addEventListener('keydown', onKeydown, true)
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true)
  document.removeEventListener('keydown', onKeydown, true)
})
</script>
